const STORAGE_KEY = "lynsight_pageview_log_v1";
const LAST_SEEN_KEY = "lynsight_pageview_log_last_seen";
export const MAX_PAGE_VIEW_LOG_ENTRIES = 2000;
export const PAGEVIEW_LOG_UPDATED_EVENT = "lynsight-pageview-log-updated";

export type PageViewLogRow = {
  id: string;
  pageUrl: string;
  /** ISO 8601 UTC, second precision */
  timestampUtc: string;
  event?: string;
  device?: string;
  deviceId?: string;
  location?: string;
};

function safeParse(raw: string | null): PageViewLogRow[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (row): row is PageViewLogRow =>
        typeof row === "object" &&
        row !== null &&
        typeof (row as PageViewLogRow).id === "string" &&
        typeof (row as PageViewLogRow).pageUrl === "string" &&
        typeof (row as PageViewLogRow).timestampUtc === "string"
    ).map((row) => ({
      ...row,
      event: typeof (row as PageViewLogRow).event === "string" ? (row as PageViewLogRow).event : undefined,
      device: typeof (row as PageViewLogRow).device === "string" ? (row as PageViewLogRow).device : undefined,
      deviceId: typeof (row as PageViewLogRow).deviceId === "string" ? (row as PageViewLogRow).deviceId : undefined,
    }));
  } catch {
    return [];
  }
}

export function readPageViewLocalLog(): PageViewLogRow[] {
  if (typeof window === "undefined") {
    return [];
  }
  return safeParse(localStorage.getItem(STORAGE_KEY));
}

export function appendPageViewToLocalLog(row: PageViewLogRow): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const prev = safeParse(localStorage.getItem(STORAGE_KEY));
    const next = [row, ...prev].slice(0, MAX_PAGE_VIEW_LOG_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(PAGEVIEW_LOG_UPDATED_EVENT));
  } catch {
    /* quota / private mode */
  }
}

export function clearPageViewLocalLog(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event(PAGEVIEW_LOG_UPDATED_EVENT));
  } catch {
    /* ignore */
  }
}

/** 로그 페이지를 마지막으로 열었던 시각(ISO UTC)을 반환. 처음 방문이면 null. */
export function readLastSeenAt(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

/** 로그 페이지를 열었음을 기록. 이후 추가된 항목만 NEW로 표시된다. */
export function saveLastSeenAt(): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
  } catch {
    /* quota / private mode */
  }
}

export function formatTimestampForDisplay(isoUtc: string): { utc: string; localKo: string } {
  const normalized = isoUtc.endsWith("Z") ? isoUtc : `${isoUtc.replace(/\.\d+/, "")}Z`;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) {
    return { utc: isoUtc, localKo: "—" };
  }
  const localKo = d.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Seoul",
  });
  return { utc: isoUtc, localKo };
}
